import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useThemeStyles } from '../../theme/useThemeStyles';
import api from '../../utils/api';

function MyFilesSection({ profile, setProfile, onUpdate, businessId }) {
  const _classes = useThemeStyles();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameFileName, setRenameFileName] = useState('');
  const [fileToRename, setFileToRename] = useState(null);
  const [renameFileDialogOpen, setRenameFileDialogOpen] = useState(false);
  const [fileToRenameId, setFileToRenameId] = useState(null);
  const [renameFileFileName, setRenameFileFileName] = useState('');
  const [storageStats, setStorageStats] = useState(null);
  const [_storageLoading, setStorageLoading] = useState(false);

  const files = profile?.files || [];

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    fetchStorageStats();
  }, [profile]);

  const fetchStorageStats = async () => {
    try {
      setStorageLoading(true);
      const response = await api.get('/api/leads/attachments/storage-stats');
      if (response.data) {
        setStorageStats(response.data);
      }
    } catch (error) {
      setStorageStats(null);
    } finally {
      setStorageLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (20MB limit)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      // Reset file input
      event.target.value = '';
      return;
    }

    // Get file name from user (show modal)
    const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
    setFileToRename(file);
    setRenameFileName(fileName);
    setRenameModalOpen(true);
    
    // Don't reset file input yet - wait for modal confirmation
  };

  const handleConfirmRename = async () => {
    if (!renameFileName || !renameFileName.trim()) {
      setError('File name cannot be empty');
      return;
    }

    if (!fileToRename) {
      return;
    }

    setRenameModalOpen(false);
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', fileToRename);
      formData.append('fileName', renameFileName.trim());

      const response = await api.post('/api/supplier/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: businessId ? { businessId: String(businessId) } : undefined
      });

      // Update profile with new file
      setProfile(prev => ({
        ...prev,
        files: [...(prev.files || []), response.data.file]
      }));

      setSuccess('File uploaded successfully!');
      
      // Refresh profile data and storage stats
      fetchStorageStats();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      // Reset modal state
      setFileToRename(null);
      setRenameFileName('');
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const handleCancelRename = () => {
    setRenameModalOpen(false);
    setRenameFileName('');
    setFileToRename(null);
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    setUploading(true);
    setError('');

    try {
      await api.delete(`/api/supplier/delete-file/${fileId}`, {
        params: businessId ? { businessId: String(businessId) } : undefined
      });
      
      // Update profile to remove file
      setProfile(prev => ({
        ...prev,
        files: (prev.files || []).filter(f => f._id !== fileId)
      }));

      setSuccess('File deleted successfully!');
      
      // Refresh profile data and storage stats
      fetchStorageStats();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete file');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateFileName = async () => {
    if (!renameFileFileName.trim()) {
      setError('File name cannot be empty');
      return;
    }

    if (!fileToRenameId) {
      return;
    }

    setUploading(true);
    setError('');

    try {
      const _response = await api.put(`/api/supplier/update-file/${fileToRenameId}`, {
        name: renameFileFileName.trim()
      }, {
        params: businessId ? { businessId: String(businessId) } : undefined
      });

      // Update profile with new file name
      setProfile(prev => ({
        ...prev,
        files: (prev.files || []).map(f => 
          f._id === fileToRenameId ? { ...f, name: renameFileFileName.trim() } : f
        )
      }));

      setSuccess('File name updated successfully!');
      setRenameFileDialogOpen(false);
      setFileToRenameId(null);
      setRenameFileFileName('');
      
      // Refresh profile data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update file name');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleShowOnProfile = async (fileId, currentValue) => {
    setError('');

    try {
      const _response = await api.put(`/api/supplier/update-file/${fileId}`, {
        showOnProfile: !currentValue
      }, {
        params: businessId ? { businessId: String(businessId) } : undefined
      });

      // Update profile with new showOnProfile value
      setProfile(prev => ({
        ...prev,
        files: (prev.files || []).map(f => 
          f._id === fileId ? { ...f, showOnProfile: !currentValue } : f
        )
      }));
      
      // Refresh profile data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update file visibility');
    }
  };

  const handlePreview = (fileUrl) => {
    setPreviewUrl(fileUrl);
    setPreviewOpen(true);
  };

  const _getFileIcon = (fileUrl) => {
    const extension = fileUrl.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <PdfIcon color="error" />;
    }
    return <FileIcon color="primary" />;
  };

  const startEditing = (file) => {
    setFileToRenameId(file._id);
    setRenameFileFileName(file.name);
    setRenameFileDialogOpen(true);
  };

  const cancelRenameFile = () => {
    setRenameFileDialogOpen(false);
    setFileToRenameId(null);
    setRenameFileFileName('');
  };

  return (
    <Box sx={{ mb: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload files such as menus, brochures, packages, and more. Toggle "Show on profile" 
            to make files visible and downloadable by couples viewing your business profile.
          </Typography>
          
          {/* File Upload */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              disabled={uploading}
              sx={{ minWidth: 150 }}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
              <input
                type="file"
                hidden
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </Button>
            
            {uploading && <CircularProgress size={20} />}
            
            <Typography variant="caption" color="text.secondary">
              PDF, DOC, DOCX, JPG, PNG (max 20MB)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Files List */}
      {files.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Uploaded Files ({files.length})
            </Typography>
            
            <List>
              {files.map((file, index) => (
                <React.Fragment key={file._id}>
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={file.showOnProfile || false}
                            onChange={() => handleToggleShowOnProfile(file._id, file.showOnProfile)}
                            size="small"
                            color="primary"
                          />
                        }
                        label={
                          <Tooltip title={file.showOnProfile ? "Visible on profile" : "Hidden from profile"}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ ml: 0.5, pr: 1.25 }}>
                                Show on profile
                              </Typography>
                            </Box>
                          </Tooltip>
                        }
                        labelPlacement="start"
                      />
                      
                      <IconButton
                        onClick={() => handlePreview(file.fileUrl)}
                        size="small"
                        color="primary"
                        title="Preview file"
                      >
                        <DownloadIcon />
                      </IconButton>
                      
                      <IconButton
                        onClick={() => startEditing(file)}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      
                      <IconButton
                        onClick={() => handleDeleteFile(file._id)}
                        size="small"
                        color="error"
                        disabled={uploading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < files.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            
            {/* Storage Usage - Discreet at bottom */}
            {storageStats && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Storage: {formatFileSize(storageStats.totalSize)} used of {formatFileSize(storageStats.storageLimit)} ({Math.round(storageStats.storageUsedPercent)}%)
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 4, bgcolor: 'divider', borderRadius: 0.5, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      bgcolor: storageStats.storageUsedPercent > 90 ? 'error.main' : storageStats.storageUsedPercent > 75 ? 'warning.main' : 'primary.main',
                      width: `${Math.min(storageStats.storageUsedPercent, 100)}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No files uploaded yet. Upload your first file above.
            </Typography>
          </CardContent>
          {/* Storage Usage - Show even when no files */}
          {storageStats && (
            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', px: 2, pb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Storage: {formatFileSize(storageStats.totalSize)} used of {formatFileSize(storageStats.storageLimit)} ({Math.round(storageStats.storageUsedPercent)}%)
                </Typography>
              </Box>
              <Box sx={{ width: '100%', height: 4, bgcolor: 'divider', borderRadius: 0.5, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: storageStats.storageUsedPercent > 90 ? 'error.main' : storageStats.storageUsedPercent > 75 ? 'warning.main' : 'primary.main',
                    width: `${Math.min(storageStats.storageUsedPercent, 100)}%`,
                    transition: 'width 0.3s ease'
                  }}
                />
              </Box>
            </Box>
          )}
        </Card>
      )}

      {/* Rename File Modal (for upload) */}
      <Dialog 
        open={renameModalOpen} 
        onClose={handleCancelRename}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Name Your File</Typography>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name for this file. This name will be visible to couples when they download the file.
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            label="File Name"
            value={renameFileName}
            onChange={(e) => setRenameFileName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleConfirmRename();
              }
            }}
            inputProps={{ maxLength: 200 }}
            helperText={`${renameFileName.length}/200 characters`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCancelRename}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmRename}
            variant="contained"
            disabled={!renameFileName || !renameFileName.trim()}
          >
            Upload File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename File Dialog (for existing files) */}
      <Dialog 
        open={renameFileDialogOpen} 
        onClose={cancelRenameFile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Rename File</Typography>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new name for this file. This name will be visible to couples when they download the file.
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            label="File Name"
            value={renameFileFileName}
            onChange={(e) => setRenameFileFileName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleUpdateFileName();
              } else if (e.key === 'Escape') {
                cancelRenameFile();
              }
            }}
            inputProps={{ maxLength: 200 }}
            helperText={`${renameFileFileName.length}/200 characters`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={cancelRenameFile}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateFileName}
            variant="contained"
            disabled={!renameFileFileName || !renameFileFileName.trim() || uploading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">File Preview</Typography>
          <IconButton onClick={() => setPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ height: '70vh', width: '100%' }}>
            {previewUrl.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={previewUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="File Preview"
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <FileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Preview not available for this file type
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  href={previewUrl}
                  target="_blank"
                >
                  Download File
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            href={previewUrl} 
            target="_blank"
            startIcon={<UploadIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyFilesSection;

